import { useCallback, useMemo, useState } from 'react';
import {
  ChakraFlex,
  CreateTreatmentEpisodeModal,
  Drawer,
  EntityType,
  FAIconName,
  InquiryDetailHeader,
  NoteMaudCardV2Props,
  useToastMessage,
} from '@equip.health/ui';
import { CreateTreatmentEpisodeModalValues } from '@equip.health/ui/lib/types/src/maud/modals/CreateTreatmentEpisode/CreateTreatmentEpisodeModal';

import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import EditInquiry from '~/components/common/inquiry/drawer/EditInquiry';
import InquiryDrawerHeader from '~/components/common/inquiry/drawer/InquiryDrawerHeader';
import { EditMode } from '~/components/common/inquiry/drawer/types';
import { useEditInquiry } from '~/components/common/inquiry/drawer/useEditInquiry';
import { useShowRequiredFieldErrors } from '~/components/common/inquiry/drawer/useShowRequiredFieldErrors';
import {
  getContactsInitialValue,
  getCoveragesInitialValue,
  getInquiryInitialValue,
  getPatientInitialValue,
  isMissingFields,
} from '~/components/common/inquiry/drawer/util';
import useEditInquiryStatus from '~/components/common/inquiry/useEditInquiryStatus';
import VoidNoteModal from '~/components/common/VoidNoteModal';
import InquiryCloseModal from '~/components/maud/inquiries/InquiryDetail/InquiryCloseModal';
import { useInquiryDetail } from '~/components/maud/inquiries/InquiryDetail/useInquiryDetail';
import { useUpdateInquiry } from '~/components/maud/inquiries/InquiryDetail/useUpdateInquiry';
import {
  ViewInquiry,
  ViewInquiryEvents,
} from '~/components/maud/inquiries/InquiryDetail/ViewInquiry';
import {
  getInquiryHeaderProps,
  InquiryDetailHeaderData,
} from '~/components/maud/inquiries/InquiryDetail/viewUtil';
import { InquiryStatusTagDropdownOptions } from '~/components/maud/inquiries/InquiryViewsTable/utils';
import EditNoteAccordion from '~/components/maud/patients/note/EditNoteAccordion';
import NewTaskAccordion from '~/components/maud/tasks/NewTaskAccordion';
import { GlobalErrorCode } from '~/lib/constants/global.constants';
import {
  InquiryStatusValue,
  NormalizeDuplicateResponseData,
} from '~/lib/constants/inquiry.constants';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import useErrorToast from '~/lib/hooks/useErrorToast';
import { Path } from '~/routes/constants';
import store from '~/store';
import { CacheTag } from '~/store/services/CacheTag';
import {
  inquiryApi,
  useConfirmPatientMutation,
  useMergeDuplicateContactsAndCoveragesMutation,
} from '~/store/services/inquiry';
import { useUpdateVoidReasonMutation } from '~/store/services/notes/notes';
import { useSetPatientActiveMutation } from '~/store/services/patients';
import { useUpdateTaskStatusMutation } from '~/store/services/task';
import {
  useGetProviderTimezonesEnumQuery,
  useGetRelationsToPatientEnumQuery,
} from '~/store/services/enumerations';
import { useLocation } from 'react-router-dom';
import { InquiryMergeDuplicates } from './mergeDuplicates';
import { useGetPatientDiagnosesQuery } from '~/store/services/diagnoses';
import { useTimezoneLabelToCodeMapper } from '~/lib/util/timezone.util';
import {
  getTreatmentEpisodePrograms,
  getTreatmentProgramRules,
  handleAdmitDateOrProgramChange,
  toLocalTz,
} from '../../patients/treatmentEpisode/util';
import { QUALIFYING_CALL } from '~/lib/constants/scheduling.constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CareTeamProviderType } from '~/lib/constants/patients.constants';
import { CREATE_TASK_SOURCES } from '~/lib/constants/task.constants';

/**
 * This drawer component is rendered via the ComponentPortalContext. As such, its appearance and behavior are completely controlled
 * via the props exposed by the "useComponentPortalContext" hook, namely "inquiryDetailDrawer" and "setComponentVisibility". Importantly,
 * any component which needs to trigger the InquiryDetailDrawer to appear or dissappear MUST use the "setComponentVisibility" function
 * to open & close the drawer.
 *
 * See the InquiryTable and GlobalSearch for usage examples.
 */

interface OnCloseProps {
  closedReasonId: string;
  noShowEmail?: boolean;
}

export const InquiryDetailDrawer = () => {
  const { currentUser } = useGlobalContext();
  const { showErrorToast } = useErrorToast();
  const { inquiryDetailDrawer, setComponentVisibility } =
    useComponentPortalContext();
  const { inquiryId } = inquiryDetailDrawer?.data ?? {};
  const inquiryDetailState = useInquiryDetail(inquiryId);
  const useEditInquiryState = useEditInquiry();
  const { pathname } = useLocation();
  const { execute, isUpdating } = useUpdateInquiry();
  const [
    mergeDuplicateContactsAndCoverages,
    { isLoading: isMergeDuplicateContactsAndCoveragesLoading },
  ] = useMergeDuplicateContactsAndCoveragesMutation();
  const [showEditContent, setShowEditContent] = useState<boolean>(false);
  const { showRequiredFieldErrors, setShowRequiredFieldErrors } =
    useShowRequiredFieldErrors();
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState<boolean>(false);
  const { data: relationsToPatientInContact } =
    useGetRelationsToPatientEnumQuery();
  const [updateTaskStatus] = useUpdateTaskStatusMutation();

  const [isAddNoteOpen, setIsAddNoteOpen] = useState<boolean>(false);
  const [isAddNoteExpanded, setIsAddNoteExpanded] = useState<boolean>(false);
  const setShowAddNote = useCallback((show: boolean) => {
    if (show) {
      setIsAddNoteOpen(true);
      // TODO is this still necessary?
      // This hack is necessary to get the EditNoteAccordion to appear correctly. Without a transition from isExpanded
      // false->true the EditNoteAccordion renders as expanded yet with no content.
      setTimeout(() => {
        setIsAddNoteExpanded(true);
      }, 0);
    } else {
      setIsAddNoteOpen(false);
      setIsAddNoteExpanded(false);
    }
  }, []);
  const [isVoidNoteOpen, setIsVoidNoteOpen] = useState<boolean>(false);
  const [editNoteId, setEditNoteId] = useState<string>(null);
  const [voidNoteId, setVoidNoteId] = useState<string>(null);
  const [
    isDuplicateReconciliationModalOpen,
    setIsDuplicateReconciliationModalOpen,
  ] = useState(false);

  const [isAddTaskOpen, setIsAddTaskOpen] = useState<boolean>(false);
  const [isAddTaskExpanded, setIsAddTaskExpanded] = useState<boolean>(false);
  const setShowAddTask = useCallback((show: boolean) => {
    if (show) {
      setIsAddTaskOpen(true);
      // This hack is necessary to get the NewTaskAccordion to appear correctly. Without a transition from isExpanded
      // false->true the NewTaskAccordion renders as expanded yet with no content.
      setTimeout(() => {
        setIsAddTaskExpanded(true);
      }, 0);
    } else {
      setIsAddTaskOpen(false);
      setIsAddTaskExpanded(false);
    }
  }, []);

  const {
    enableAdmittingTreatmentProgram: isAdmittingTreatmentProgramEnabled,
  } = useFlags<LDFlags>();

  const [triggerConfirmPatient] = useConfirmPatientMutation();
  const [triggerSetPatientActive] = useSetPatientActiveMutation();
  const TOPLEVEL_ID = 'inquiry-detail';
  const useEditInquiryStatusState = useEditInquiryStatus({
    currentStatus: inquiryDetailState?.inquiry?.status?.name,
    showConflictErrorModal: false,
    source: 'Inquiry Detail',
  });
  const { data: diagnoses } = useGetPatientDiagnosesQuery(
    inquiryDetailState?.patient?.userId,
    { skip: !inquiryDetailState?.patient?.userId },
  );
  const primaryDiagnosis = useMemo(() => {
    const primary = diagnoses?.find((d) => d.primary);
    return (
      primary?.diagnosisAbbreviation || primary?.diagnosis?.shortDescription
    );
  }, [diagnoses]);
  const { data: timezones } = useGetProviderTimezonesEnumQuery();
  const timezoneLabelToCodeMapper = useTimezoneLabelToCodeMapper(timezones);
  const timezoneCode =
    timezoneLabelToCodeMapper[inquiryDetailState?.patient?.timezone];
  const headerProps = useMemo<InquiryDetailHeaderData>(() => {
    const result: InquiryDetailHeaderData = getInquiryHeaderProps({
      inquiry: inquiryDetailState.inquiry,
      patient: inquiryDetailState.patient,
      primaryDiagnosis,
      timezoneCode,
    });
    if (!isAdmittingTreatmentProgramEnabled) {
      result.targetTreatmentProgram = null;
    }
    return result;
  }, [
    inquiryDetailState.inquiry,
    inquiryDetailState.patient,
    primaryDiagnosis,
    timezoneCode,
    isAdmittingTreatmentProgramEnabled,
  ]);
  const [updateVoidReason] = useUpdateVoidReasonMutation();

  const [headerKeyCounter, setHeaderKeyCounter] = useState<number>(0);

  const makePatientActive = (isActive: boolean) => {
    triggerSetPatientActive({
      isActive,
      patientId: inquiryDetailState.patient.userId,
    })
      .unwrap()
      .catch((err) => {
        showErrorToast(
          GlobalErrorCode.SET_PATIENT_ACTIVE,
          err.status,
          err.data?.message,
        );
      });
  };

  const ENTITY_TYPE_RANK: Record<EntityType, number> = {
    [EntityType.CONTACT]: 0,
    [EntityType.REFERRER]: 1,
    [EntityType.COVERAGE]: 2,
  };

  const sortDuplicateRecords = (records: NormalizeDuplicateResponseData[]) => {
    return records.sort((a, b) => {
      const rankA = ENTITY_TYPE_RANK[a.baseEntity.entityType] ?? 99;
      const rankB = ENTITY_TYPE_RANK[b.baseEntity.entityType] ?? 99;
      return rankA - rankB;
    });
  };

  const {
    fetchDuplicateContactsLoading,
    fetchDuplicateCoveragesLoading,
    isPatientMatchConfirmed,
  } = inquiryDetailState;
  const { createToast } = useToastMessage();
  const [duplicateRecords, setDuplicateRecords] =
    useState<NormalizeDuplicateResponseData[]>(null);

  const refreshDuplicateRecords = (
    duplicatesData: NormalizeDuplicateResponseData[],
    entityType: EntityType.CONTACT | EntityType.COVERAGE,
  ) => {
    setDuplicateRecords((prev: NormalizeDuplicateResponseData[]) => {
      const previousContacts = (prev ?? []).filter(
        (d) => d.baseEntity.entityType !== EntityType.COVERAGE,
      );
      const previousCoverages = (prev ?? []).filter(
        (d) => d.baseEntity.entityType === EntityType.COVERAGE,
      );
      const newContacts =
        entityType === EntityType.CONTACT ? duplicatesData : previousContacts;
      const newCoverages =
        entityType === EntityType.COVERAGE ? duplicatesData : previousCoverages;
      return newContacts.concat(newCoverages);
    });
  };

  const handleMatchesFound = (
    duplicatesData: NormalizeDuplicateResponseData[],
    entityType: EntityType,
  ) => {
    if (!duplicatesData.some((d) => d.matchedEntities?.length > 0)) {
      createToast({
        id: `duplicate-${entityType}s-toast`,
        text: `No duplicate ${entityType}s found`,
        variant: 'warning',
      });
    } else {
      setIsDuplicateReconciliationModalOpen(true);
    }
  };

  const fetchDuplicateData = async (
    patientId: string,
    entityType: EntityType.CONTACT | EntityType.COVERAGE,
    fetchDuplicatesFn: (
      patientId: string,
    ) => Promise<NormalizeDuplicateResponseData[]>,
  ) => {
    try {
      const duplicates = (await fetchDuplicatesFn(patientId)) ?? [];
      refreshDuplicateRecords(duplicates, entityType);
      handleMatchesFound(duplicates, entityType);
    } catch (err) {
      const globalErrorCode =
        entityType === EntityType.CONTACT
          ? GlobalErrorCode.FETCH_DUPLICATE_CONTACTS
          : GlobalErrorCode.FETCH_DUPLICATE_COVERAGES;
      showErrorToast(globalErrorCode, err.status, err.data?.message);
    }
  };

  const fetchDuplicateContacts = async (patientId: string) =>
    fetchDuplicateData(
      patientId,
      EntityType.CONTACT,
      inquiryDetailState.fetchDuplicateContacts,
    );

  const fetchDuplicateCoverages = async (patientId: string) =>
    fetchDuplicateData(
      patientId,
      EntityType.COVERAGE,
      inquiryDetailState.fetchDuplicateCoverages,
    );

  const confirmPatient = (inquiryId: string, patientId: string) => {
    triggerConfirmPatient({
      inquiryId,
      patientId,
    })
      .unwrap()
      .then(async () => {
        const [duplicateContacts, duplicateCoverages] = await Promise.all([
          inquiryDetailState.fetchDuplicateContacts(patientId),
          inquiryDetailState.fetchDuplicateCoverages(patientId),
        ]);

        const combined = sortDuplicateRecords([
          ...duplicateContacts,
          ...duplicateCoverages,
        ]);
        setDuplicateRecords(combined);
        if (
          duplicateContacts?.[0]?.matchedEntities?.[0] ||
          duplicateCoverages?.[0]?.matchedEntities?.[0]
        ) {
          setIsDuplicateReconciliationModalOpen(true);
        }
      })
      .catch((err) => {
        showErrorToast(
          GlobalErrorCode.CONFIRM_PATIENT,
          err.status,
          err.data?.message,
        );
      });
  };

  const onAssignCareTeamProvider = useCallback(
    (providerType?: CareTeamProviderType) => {
      setComponentVisibility({
        component: PortalComponent.PROVIDER_ASSIGNMENT_MODAL,
        data: {
          inquiryId,
          patientId: inquiryDetailState?.patient?.userId,
          providerTypes: [providerType],
        },
        isVisible: true,
      });
    },
    [inquiryId, inquiryDetailState?.patient?.userId, setComponentVisibility],
  );

  const eventHandlers: ViewInquiryEvents = {
    onAddNote: () => {
      setShowAddNote(true);
    },
    onAddTask: () => {
      setShowAddTask(true);
    },
    onAssignCareTeamProvider,
    onChangeTaskStatus: (
      taskId: string,
      status: string,
      voidReasonExternalId?: string,
    ) => {
      updateTaskStatus({
        inquiryId,
        taskId,
        taskStatusCode: status,
        voidReasonExternalId,
      });
    },
    onConfirmPatient: (patientId: string) => {
      confirmPatient(inquiryId, patientId);
    },
    onCreatePatientChart: () => {
      makePatientActive(true);
      confirmPatient(inquiryId, inquiryDetailState.patient.userId);
    },
    onEditNote: (note: NoteMaudCardV2Props) => {
      setEditNoteId(note.id);
      setShowAddNote(true);
    },
    onFetchDuplicateContacts: () =>
      fetchDuplicateContacts(inquiryDetailState.patient.userId),
    onFetchDuplicateCoverages: () =>
      fetchDuplicateCoverages(inquiryDetailState.patient.userId),
    onLoadMoreNotes: () => {
      inquiryDetailState.loadMoreNotes();
    },
    onPatientClick: () => {
      window.open(
        `${Path.MAUD_PATIENTS}/${inquiryDetailState.patient.userId}`,
        '_blank',
        'noopener',
      );
    },
    onVoidNote: (note: NoteMaudCardV2Props) => {
      setVoidNoteId(note.id);
      setIsVoidNoteOpen(true);
    },
  };

  const onStatusModalCancel = () => {
    useEditInquiryStatusState.cancel();
    setHeaderKeyCounter(headerKeyCounter + 1);
  };

  const closeDrawer = () => {
    setShowRequiredFieldErrors(false);
    setIsExitConfirmOpen(false);
    setShowEditContent(false);
    setShowAddNote(false);
    setShowAddTask(false);
    inquiryDetailDrawer.data?.onClose?.();
  };

  const onChangeStatus = (tagId: string) => {
    const patientId = inquiryDetailState.patient?.userId;
    useEditInquiryStatusState
      .changeStatus(
        inquiryId,
        tagId,
        inquiryDetailState.inquiry.version,
        pathname,
        patientId,
      )
      .catch((error) => {
        showErrorToast(
          GlobalErrorCode.EDIT_INQUIRY_V2,
          error.status,
          error.data?.message,
        );
      });
  };

  const onEnrollInquiry = async ({
    admitDate,
    endDate,
    treatmentProgram,
  }: CreateTreatmentEpisodeModalValues) => {
    useEditInquiryStatusState
      .enroll(
        inquiryId,
        toLocalTz(admitDate),
        inquiryDetailState.inquiry.version,
        getTreatmentEpisodePrograms(admitDate, treatmentProgram, endDate),
        pathname,
        inquiryDetailState.patient.userId,
      )
      .catch((error) => {
        showErrorToast(
          GlobalErrorCode.EDIT_INQUIRY_V2,
          error.status,
          error.data?.message,
        );
      });
  };

  const onCloseHandler = () => {
    if (showEditContent) {
      setIsExitConfirmOpen(true);
    } else {
      closeDrawer();
    }
  };

  const onEdit = () => {
    const {
      updateReferral,
      updatePatient,
      updateCoverage,
      updateInfo,
      updateContact,
      updateNote,
      resetState,
      addContact,
    } = useEditInquiryState;

    const [referral, contacts] = getContactsInitialValue(
      inquiryDetailState.contacts,
      relationsToPatientInContact,
    );
    resetState();
    const coverages = getCoveragesInitialValue(inquiryDetailState.coverages);
    updateReferral(referral);
    contacts.forEach((contact, index) => {
      if (index > 0) {
        // There will always be one contact. Each additional one needs to be created.
        addContact();
      }
      updateContact(index, contact);
    });

    updateCoverage(coverages.length > 0 ? coverages[0] : {});
    updatePatient(
      getPatientInitialValue(
        inquiryDetailState.patient,
        inquiryDetailState.inquiry.primaryContact?.id,
      ),
    );
    updateInfo(getInquiryInitialValue(inquiryDetailState.inquiry));
    setShowEditContent(true);
    updateNote({ content: '', isQualifyingCall: false });
  };

  const onSave = () => {
    const { contacts, coverage, info, patient, note, referral } =
      useEditInquiryState;

    if (
      isMissingFields({
        contacts,
        coverages: coverage,
        info,
        note,
        patient,
        referral,
      })
    ) {
      setShowRequiredFieldErrors(true);
      return;
    }
    execute(
      inquiryId,
      currentUser.userId,
      pathname,
      inquiryDetailState.patient.userId,
      {
        contacts,
        coverages: coverage,
        info,
        note,
        patient,
        referral,
      },
      {
        contacts: inquiryDetailState.contacts,
        coverage: inquiryDetailState.coverages?.[0],
        patient: inquiryDetailState.patient,
        version: inquiryDetailState.inquiry.version,
      },
    )
      .then(() => {
        setShowRequiredFieldErrors(false);
        setShowEditContent(false);
      })
      .catch((error) => {
        console.log(error);
        showErrorToast(
          GlobalErrorCode.EDIT_INQUIRY_V2,
          error.status,
          error.data?.message,
        );
      });
  };

  const isDrawerOpen = Boolean(inquiryId);

  const onCloseConfirm = ({ closedReasonId, noShowEmail }: OnCloseProps) => {
    useEditInquiryStatusState
      .closeInquiry({
        closedReasonId,
        inquiryId,
        noShowEmail,
        pathname,
        patientId: inquiryDetailState.patient.userId,
        version: inquiryDetailState.inquiry.version,
      })
      .catch((error) => {
        showErrorToast(
          GlobalErrorCode.EDIT_INQUIRY_V2,
          error.status,
          error.data?.message,
        );
      });
  };

  const handleMergeDuplicatesClose = () => {
    setIsDuplicateReconciliationModalOpen(false);
  };

  const handleMergeDuplicates = async (
    formattedRequest: MergeDuplicateContactsAndCoveragesRequestParams,
  ) => {
    try {
      handleMergeDuplicatesClose();
      await mergeDuplicateContactsAndCoverages(formattedRequest).unwrap();
      createToast({
        icon: FAIconName.checkRegular,
        id: 'merge-duplicates-success-toast',
        text: 'Merged duplicates successfully',
        variant: 'success',
      });
    } catch (error) {
      showErrorToast(
        GlobalErrorCode.MERGE_DUPLICATE_CONTACTS,
        error.status,
        error.data?.message,
      );
    }
  };

  const header = showEditContent ? (
    <InquiryDrawerHeader
      isDisabled={false}
      isLoading={isUpdating}
      onSave={onSave}
      title="Edit inquiry"
    />
  ) : (
    <ChakraFlex marginRight="44px">
      <InquiryDetailHeader
        editDisabled={inquiryDetailState.isLoading}
        isLoading={useEditInquiryStatusState.isSaving}
        isStatusReadOnly={
          inquiryDetailState.inquiry?.status?.name ===
          InquiryStatusValue.ENROLLED
        }
        key={headerKeyCounter}
        onChangeStatus={onChangeStatus}
        onEdit={onEdit}
        statusOptions={InquiryStatusTagDropdownOptions}
        {...headerProps}
        status={inquiryDetailState?.inquiry?.status?.name}
      />
    </ChakraFlex>
  );

  const content = showEditContent ? (
    <EditInquiry
      editInquiryState={useEditInquiryState}
      editMode={EditMode.EDIT}
      showRequiredFieldErrors={showRequiredFieldErrors}
    />
  ) : (
    <ViewInquiry
      eventHandlers={eventHandlers}
      id={TOPLEVEL_ID}
      inquiryDetailState={inquiryDetailState}
    />
  );

  const hasAnyQualifyingCallNote = inquiryDetailState?.notes?.data?.some(
    (note: any) => note.noteType === QUALIFYING_CALL,
  );

  const targetTreatmentProgram = isAdmittingTreatmentProgramEnabled
    ? inquiryDetailState?.inquiry?.targetTreatmentProgram?.description
    : undefined;
  const defaultAdmitDate = new Date();

  return (
    <Drawer
      header={header}
      id={TOPLEVEL_ID}
      isOpen={isDrawerOpen}
      onClose={onCloseHandler}
    >
      {content}
      <DiscardChangesModalV2
        isOpen={isExitConfirmOpen}
        onClose={() => setIsExitConfirmOpen(false)}
        onConfirm={() => {
          setIsExitConfirmOpen(false);
          setShowEditContent(false);
        }}
      />
      <InquiryCloseModal
        appointments={inquiryDetailState?.appointments}
        isNotesEmpty={!hasAnyQualifyingCallNote}
        isOpen={useEditInquiryStatusState.showClosedReasonModal}
        onCancel={onStatusModalCancel}
        onConfirm={onCloseConfirm}
      />
      {/* Force the CreateTreatmentEpisodeModal to remount on open to keep default state values up to date */}
      {useEditInquiryStatusState.showCreateTreatmentEpisodeModal && (
        <CreateTreatmentEpisodeModal
          defaultAdmitDate={defaultAdmitDate}
          defaultEndDate={handleAdmitDateOrProgramChange({
            admitDate: defaultAdmitDate,
            treatmentProgram: targetTreatmentProgram,
          })}
          defaultTreatmentProgram={targetTreatmentProgram}
          isOpen
          onCancel={onStatusModalCancel}
          onChangeAdmitDate={handleAdmitDateOrProgramChange}
          onChangeTreatmentProgram={handleAdmitDateOrProgramChange}
          onConfirm={onEnrollInquiry}
          programRules={getTreatmentProgramRules(
            useEditInquiryStatusState.treatmentProgramOptions,
          )}
        />
      )}
      {isAddNoteOpen && (
        <EditNoteAccordion
          appointmentDetails={null}
          dismissAccordion={() => {
            setEditNoteId(null);
            setShowAddNote(false);
          }}
          existingNoteId={editNoteId}
          inquiryId={inquiryId}
          isExpanded={isAddNoteExpanded}
          isSessionListEnabled={false}
          patientId={inquiryDetailState?.inquiry?.patient?.id}
          setIsExpanded={setIsAddNoteExpanded}
        />
      )}

      {isPatientMatchConfirmed && isDuplicateReconciliationModalOpen && (
        <InquiryMergeDuplicates
          duplicateRecords={duplicateRecords}
          id={inquiryId}
          isLoadingDuplicates={
            fetchDuplicateContactsLoading || fetchDuplicateCoveragesLoading
          }
          isMergingDuplicates={isMergeDuplicateContactsAndCoveragesLoading}
          isOpen
          onClose={handleMergeDuplicatesClose}
          onSubmitMerge={handleMergeDuplicates}
        />
      )}

      {isVoidNoteOpen && (
        <VoidNoteModal
          isOpen={isVoidNoteOpen}
          onConfirm={(n) => {
            updateVoidReason({
              data: {
                inquiryId,
                noteExternalId: voidNoteId,
                voidReason: n.voidReason,
              },
            }).catch((error) => {
              showErrorToast(
                GlobalErrorCode.VOID_NOTE,
                error.status,
                error.data?.message,
              );
            });
          }}
          setIsOpen={setIsVoidNoteOpen}
        />
      )}
      {isAddTaskOpen && (
        <NewTaskAccordion
          defaultCategoryName="Admissions"
          defaultPatient={inquiryDetailState.patient}
          dismissAddTask={() => {
            setShowAddTask(false);
            // TODO: When tasks accordion uses RTK Query this can be removed
            store.dispatch(
              inquiryApi.util.invalidateTags([
                { id: inquiryId, type: CacheTag.InquiryTasksById },
              ]),
            );
          }}
          triggerSource={CREATE_TASK_SOURCES.INQUIRY_DETAIL}
          inquiryId={inquiryId}
          isExpanded={isAddTaskExpanded}
          setIsExpanded={setIsAddTaskExpanded}
          showLinkedPatient
        />
      )}
    </Drawer>
  );
};
